import { CoveredLegs, EffectiveRegistrationStatus } from '@/constants/ds-attendance.constants'
import type { SendRecordContext } from '@/components/designSystem/modals/SendRecordModal/types'
import type { RegistrationSettings } from '@/Network/Services/DsAttendanceService'
import type { PriorityPaymentRow } from '@/Network/Types/Responses/PriorityPaymentsResponses'

import { priorityStationDateIso } from './rowFormat'

/** Registro aberto = enviar de novo cairia no 409 `ACTIVITY_RECORD_ALREADY_OPEN`. */
const OPEN_RECORD_STATUSES = new Set(['PENDING_AUDIT', 'AUDIT_IN_PROGRESS'])

/** Perna sem origem, a partir do check da linha. `null` = nada falta ou não se sabe. */
const missingLegsOf = (row: PriorityPaymentRow): CoveredLegs | null => {
  if (row.check.status === 'NONE') return CoveredLegs.BOTH
  if (row.check.status !== 'PARTIAL') return null
  if (!row.check.checkinSource && !row.check.checkoutSource) return CoveredLegs.BOTH
  if (!row.check.checkinSource) return CoveredLegs.CHECKIN
  if (!row.check.checkoutSource) return CoveredLegs.CHECKOUT
  return null
}

/**
 * Contexto do `SendRecordModal` aberto pela fila (BUG-UI-03): o mesmo formato do
 * Detalhes do Plantão (`stationDetailsModal.vue`), montado com o que a linha já
 * traz — médico, unidade, escala, horário na hora de parede + fuso, e o check —
 * mais a configuração de registro da escala (`GET registration-settings` com o
 * `scheduleId`). É a política que decide se o passo 1 bloqueia a folha
 * (escala só-app). Sem configuração (`null`), o modal segue oferecendo as duas
 * categorias, como antes.
 */
export const buildPrioritySendRecordContext = (
  row: PriorityPaymentRow,
  settings: RegistrationSettings | null,
  companyId: number | null
): SendRecordContext => {
  const referenceDate = priorityStationDateIso(row)
  const expectedStart = `${row.startLocal}${row.timezone}`
  return {
    doctorStationId: row.doctorStationId,
    scheduleId: row.scheduleId,
    companyId,
    healthUnitId: row.healthUnitId,
    referenceDate,
    station: {
      doctorStationId: row.doctorStationId,
      // ISO com offset (não `yyyy-MM-dd`): o cabeçalho do passo 1 formata com
      // `new Date()`, que leria a data pura como meia-noite UTC e voltaria um dia.
      referenceDate: expectedStart,
      expectedStart,
      expectedEnd: `${row.endLocal}${row.timezone}`,
      doctorName: row.doctorName,
      scheduleId: row.scheduleId,
      scheduleName: row.scheduleName,
      healthUnitName: row.healthUnitName,
      timezone: row.timezone,
      registrationStatus: row.check.status as EffectiveRegistrationStatus,
      missingLegs: missingLegsOf(row),
      hasOpenActivityRecord: OPEN_RECORD_STATUSES.has(row.activityRecord?.status ?? '')
    },
    acceptedActivityTypes: settings?.acceptedActivityTypes,
    checkinLateToleranceMinutes: settings?.checkinLateToleranceMinutes ?? null,
    checkoutEarlyToleranceMinutes: settings?.checkoutEarlyToleranceMinutes ?? null,
    policyMonitoringType: settings?.monitoringType ?? null,
    hasSheetRuleInEffect: settings?.hasSheetRuleInEffect ?? null
  }
}
